import { useEffect, useState } from 'react'

// Adapted from https://usehooks-ts.com/react-hook/use-script

export type Status = 'idle' | 'loading' | 'ready' | 'error'

export function useScript(
  src: string | null,
  defer = false,
  crossOrigin: 'anonymous' | 'use-credentials' | undefined,
): Status {
  const [status, setStatus] = useState<Status>(src ? 'loading' : 'idle')

  useEffect(() => {
    if (!src) {
      setStatus('idle')
      return
    }

    setStatus('loading')

    // Get existing script element by src if it already exists
    let script: HTMLScriptElement | null = document.querySelector(`script[src="${src}"]`)

    if (script) {
      const existingStatus = script.getAttribute('data-status')
      setStatus(existingStatus === 'ready' || existingStatus === 'error' ? existingStatus : 'loading')
    } else {
      script = document.createElement('script')
      script.src = src
      script.async = true
      script.type = 'text/javascript'
      script.defer = defer
      if (crossOrigin) {
        script.crossOrigin = crossOrigin
      }

      script.setAttribute('data-status', 'loading')
      document.body.appendChild(script)

      // Store script state as an attribute
      const setAttributeFromEvent = (event: Event) => {
        script?.setAttribute('data-status', event.type === 'load' ? 'ready' : 'error')
      }
      script.addEventListener('load', setAttributeFromEvent)
      script.addEventListener('error', setAttributeFromEvent)
    }

    // Update the state for this instance of the hook
    const setStateFromEvent = (event: Event) => {
      setStatus(event.type === 'load' ? 'ready' : 'error')
    }
    script.addEventListener('load', setStateFromEvent)
    script.addEventListener('error', setStateFromEvent)

    return () => {
      if (script) {
        script.removeEventListener('load', setStateFromEvent)
        script.removeEventListener('error', setStateFromEvent)
      }
    }
  }, [src, defer, crossOrigin])

  return status
}

export default useScript
